/**
 * Pathfinding.ts
 * A* 8x8 Grid Pathfinder with Manhattan Heuristic, Obstacle Collision Avoidance,
 * and Optimal Furniture Access Tile Resolution.
 */

import { GridCoord } from './IsometricEngine';

export interface PathNode {
  col: number;
  row: number;
  g: number;
  h: number;
  f: number;
  parent: PathNode | null;
}

export class Pathfinding {
  /**
   * Calculates shortest obstacle-free path from start to target using A*.
   *
   * @param start Starting tile coordinate
   * @param target Target tile coordinate
   * @param obstacles Set of serialized obstacle coords ("${col},${row}")
   * @param gridSize Dimension of grid (default 8 for 8x8)
   * @returns Array of coordinates representing the step-by-step path (inclusive of start and target),
   *          or empty array if no reachable path exists.
   */
  public static findPath(
    start: GridCoord,
    target: GridCoord,
    obstacles: Set<string> = new Set(),
    gridSize: number = 8
  ): GridCoord[] {
    // 1. Boundary & trivial cases
    if (
      start.col < 0 || start.col >= gridSize ||
      start.row < 0 || start.row >= gridSize ||
      target.col < 0 || target.col >= gridSize ||
      target.row < 0 || target.row >= gridSize
    ) {
      return [];
    }

    if (start.col === target.col && start.row === target.row) {
      return [{ col: start.col, row: start.row }];
    }

    const targetKey = `${target.col},${target.row}`;
    if (obstacles.has(targetKey)) {
      return []; // Target itself is blocked
    }

    const openList: PathNode[] = [];
    const openMap = new Map<string, PathNode>();
    const closedSet = new Set<string>();

    const hStart = Math.abs(start.col - target.col) + Math.abs(start.row - target.row);
    const startNode: PathNode = {
      col: start.col,
      row: start.row,
      g: 0,
      h: hStart,
      f: hStart,
      parent: null,
    };

    openList.push(startNode);
    openMap.set(`${start.col},${start.row}`, startNode);

    // 4 orthogonal directions: (+1, 0), (-1, 0), (0, +1), (0, -1)
    const deltas = [
      { dCol: 1, dRow: 0 },
      { dCol: -1, dRow: 0 },
      { dCol: 0, dRow: 1 },
      { dCol: 0, dRow: -1 },
    ];

    while (openList.length > 0) {
      // Find node with minimum fScore (break ties with smaller h)
      let lowestIdx = 0;
      for (let i = 1; i < openList.length; i++) {
        if (
          openList[i].f < openList[lowestIdx].f ||
          (openList[i].f === openList[lowestIdx].f && openList[i].h < openList[lowestIdx].h)
        ) {
          lowestIdx = i;
        }
      }

      const current = openList.splice(lowestIdx, 1)[0];
      const currentKey = `${current.col},${current.row}`;
      openMap.delete(currentKey);
      closedSet.add(currentKey);

      // Goal reached
      if (current.col === target.col && current.row === target.row) {
        return this.reconstructPath(current);
      }

      for (const d of deltas) {
        const neighborCol = current.col + d.dCol;
        const neighborRow = current.row + d.dRow;
        const neighborKey = `${neighborCol},${neighborRow}`;

        if (
          neighborCol < 0 || neighborCol >= gridSize ||
          neighborRow < 0 || neighborRow >= gridSize
        ) {
          continue; // Outside grid
        }

        if (obstacles.has(neighborKey) || closedSet.has(neighborKey)) {
          continue; // Colliding or already evaluated
        }

        const tentativeG = current.g + 1;
        const existingNode = openMap.get(neighborKey);

        if (!existingNode) {
          const h = Math.abs(neighborCol - target.col) + Math.abs(neighborRow - target.row);
          const newNode: PathNode = {
            col: neighborCol,
            row: neighborRow,
            g: tentativeG,
            h,
            f: tentativeG + h,
            parent: current,
          };
          openList.push(newNode);
          openMap.set(neighborKey, newNode);
        } else if (tentativeG < existingNode.g) {
          existingNode.g = tentativeG;
          existingNode.f = tentativeG + existingNode.h;
          existingNode.parent = current;
        }
      }
    }

    return []; // No path found
  }

  /**
   * Finds the best walkable adjacent access tile to a multi-cell furniture footprint.
   *
   * @param target Furniture top-left anchor (gridCol, gridRow)
   * @param footprint Dimensions of furniture { width, height }
   * @param playerPos Current position of the player character
   * @param obstacles All blocked cells
   * @param gridSize Dimension of grid
   */
  public static findNearestAccessTile(
    target: GridCoord,
    footprint: { width: number; height: number },
    playerPos: GridCoord,
    obstacles: Set<string>,
    gridSize: number = 8
  ): GridCoord | null {
    const accessCandidates: GridCoord[] = [];
    const width = footprint.width || 1;
    const height = footprint.height || 1;

    // Check cells along top and bottom rows
    for (let c = target.col; c < target.col + width; c++) {
      // Above
      const topRow = target.row - 1;
      if (topRow >= 0 && !obstacles.has(`${c},${topRow}`)) {
        accessCandidates.push({ col: c, row: topRow });
      }
      // Below
      const btmRow = target.row + height;
      if (btmRow < gridSize && !obstacles.has(`${c},${btmRow}`)) {
        accessCandidates.push({ col: c, row: btmRow });
      }
    }

    // Check cells along left and right columns
    for (let r = target.row; r < target.row + height; r++) {
      // Left
      const leftCol = target.col - 1;
      if (leftCol >= 0 && !obstacles.has(`${leftCol},${r}`)) {
        accessCandidates.push({ col: leftCol, row: r });
      }
      // Right
      const rightCol = target.col + width;
      if (rightCol < gridSize && !obstacles.has(`${rightCol},${r}`)) {
        accessCandidates.push({ col: rightCol, row: r });
      }
    }

    if (accessCandidates.length === 0) {
      return null;
    }

    // Sort by shortest path or Manhattan distance to playerPos
    accessCandidates.sort((a, b) => {
      const distA = Math.abs(a.col - playerPos.col) + Math.abs(a.row - playerPos.row);
      const distB = Math.abs(b.col - playerPos.col) + Math.abs(b.row - playerPos.row);
      return distA - distB;
    });

    return accessCandidates[0];
  }

  private static reconstructPath(node: PathNode): GridCoord[] {
    const path: GridCoord[] = [];
    let curr: PathNode | null = node;
    while (curr) {
      path.unshift({ col: curr.col, row: curr.row });
      curr = curr.parent;
    }
    return path;
  }
}
